// ─── ReviewPipelineFunnel ───────────────────────────────────────────────────
// Slide-style funnel visual for the Review Pipeline tab (and the Home page
// dashboard). Layout mirrors the 2025 REVIEW slide:
//
//   [ Total Opportunities ]
//       │
//   ┌───┴────────┬────────────┐
//  Dead       Tracking      Active
//  ┌──┬──┐               ┌──┬──┐
// Off  On               Off  On
//  │    │                 │   │
// LOI  LOI               LOI  LOI
//  │    │                 │   │
// B&F  B&F               B&F  B&F
//
// Clicking any lane / cell fires `onSelect({ lane, split, tier, stage })`
// so the Deals table below can filter to that bucket. Data comes from
// computeReviewPipelineFunnel — this component is purely presentational.
//
// `variant` controls the color palette:
//   • "dark"  (default) — used on the Review Pipeline page (dark app)
//   • "light" — used on the Home dashboard, matches the app's light shell
//     with PP brand accents

const DEAD_STAGES = ["Passed", "Lost", "Dead"];
const ACTIVE_STAGES_SHOWN = ["Screening", "Underwriting", "LOI Submitted", "In Diligence"];

const PALETTES = {
  dark: {
    root:      "bg-[rgba(36,36,36,0.6)] border border-[rgba(58,58,58,0.5)]",
    laneCard:  "bg-[rgba(44,44,44,0.5)] border",
    laneAcc: {
      dead:     "border-[rgba(233,53,41,0.4)]",
      tracking: "border-[rgba(240,79,68,0.4)]",
      active:   "border-[rgba(79,157,118,0.4)]",
    },
    laneHdr: {
      dead:     "bg-[rgba(233,53,41,0.15)] text-[#f4827a]",
      tracking: "bg-[rgba(240,79,68,0.15)] text-[#f4827a]",
      active:   "bg-[rgba(79,157,118,0.15)] text-[#7cbb99]",
    },
    labelDim:  "text-[#8a8a8a]",
    labelSub:  "text-[#8a8a8a]",
    valueBold: "text-white",
    // Tiers escalate down the funnel so the deepest one carries the most
    // weight: Total is quiet, LOIs are mid, Best & Final inverts to a light
    // fill with dark text so it pops off the dark ground.
    chipTier: {
      total: "bg-[rgba(44,44,44,0.5)] border border-[rgba(58,58,58,0.5)] text-[#b4b4b4] hover:border-pp-red/60",
      lois:  "bg-[rgba(58,58,58,0.6)] border border-[rgba(74,74,74,0.7)] text-[#f4f4f4] hover:border-pp-red/60",
      bf:    "bg-[#d8d8d8] border border-[#e4e4e4] text-[#333333] hover:border-pp-red",
    },
    chipTierValue: {
      total: "text-white",
      lois:  "text-white",
      bf:    "text-[#333333]",
    },
    chipMute:  "bg-[rgba(36,36,36,0.6)] border border-[rgba(58,58,58,0.4)] text-[#8a8a8a]",
    chipActive:"bg-pp-red border border-pp-red text-white",
    stageRowBase:   "text-[#b4b4b4] hover:bg-[rgba(44,44,44,0.6)]",
    stageRowActive: "bg-pp-red/25 border border-pp-red/60 text-white",
    divider:   "border-[rgba(58,58,58,0.4)]",
    tagBg:     "bg-pp-red/20 border border-pp-red/40 text-pp-red",
    tagBtn:    "text-[#8a8a8a] hover:text-[#f4f4f4]",
    total:     "text-white",
    totalNote: "text-[#8a8a8a]",
  },
  light: {
    root:      "bg-white border border-[#d8d8d8] shadow-sm",
    laneCard:  "bg-white border-2",
    laneAcc: {
      dead:     "border-pp-red/30",
      tracking: "border-[rgba(240,79,68,0.5)]",
      active:   "border-[rgba(79,157,118,0.4)]",
    },
    laneHdr: {
      dead:     "bg-pp-red/10 text-pp-red",
      tracking: "bg-[rgba(233,53,41,0.12)] text-[#a5241b]",
      active:   "bg-[rgba(79,157,118,0.16)] text-[#2f6b4d]",
    },
    labelDim:  "text-[#8a8a8a]",
    labelSub:  "text-[#8a8a8a]",
    valueBold: "text-[#333333]",
    // Same escalation on the light ground: Best & Final takes the dark slate
    // fill with white type, so the deepest tier is the one that reads first.
    chipTier: {
      total: "bg-[#f4f4f4] border border-[#d8d8d8] text-[#7a7a7a] hover:border-pp-red/60",
      lois:  "bg-[#d8d8d8] border border-[#c4c4c4] text-[#555555] hover:border-pp-red/60",
      bf:    "bg-[#3a3a3a] border border-[#3a3a3a] text-white hover:border-pp-red",
    },
    chipTierValue: {
      total: "text-[#333333]",
      lois:  "text-[#333333]",
      bf:    "text-white",
    },
    chipMute:  "bg-[#f4f4f4] border border-[#d8d8d8] text-[#8a8a8a]",
    chipActive:"bg-pp-red border border-pp-red text-white",
    stageRowBase:   "text-[#555555] hover:bg-[#ebebeb]",
    stageRowActive: "bg-pp-red/15 border border-pp-red text-pp-red",
    divider:   "border-[#d8d8d8]",
    tagBg:     "bg-pp-red/10 border border-pp-red/40 text-pp-red",
    tagBtn:    "text-[#8a8a8a] hover:text-[#3f3f3f]",
    total:     "text-[#333333]",
    totalNote: "text-[#8a8a8a]",
  },
};

const LANE_NAME = { dead: "Dead", tracking: "Tracking", active: "Active" };

// `tier` selects the escalating fill — total (quiet) → lois (mid) → bf
// (strongest). A zero value always falls back to the muted style regardless
// of tier, and the selected state overrides everything with the brand red.
function Chip({ label, value, active, muted, onClick, palette, tier = "total" }) {
  const isBtn = typeof onClick === "function" && value > 0;
  const base = "flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors";
  const cls = active
    ? palette.chipActive
    : muted
      ? palette.chipMute
      : palette.chipTier[tier];
  const valueCls = active || muted ? "" : palette.chipTierValue[tier];
  const Node = isBtn ? "button" : "div";
  return (
    <Node onClick={isBtn ? onClick : undefined} className={`${base} ${cls}`}>
      <span>{label}</span>
      <span className={`font-bold tabular-nums ${valueCls}`}>{value ?? 0}</span>
    </Node>
  );
}

function LaneSplitCol({ label, split, laneKey, splitKey, onSelect, selection, palette }) {
  const isSel = (tier) => selection?.lane === laneKey && selection?.split === splitKey && selection?.tier === tier;
  return (
    <div className="flex flex-col gap-1.5">
      <div className={`text-[10px] uppercase tracking-wider font-bold ${palette.labelDim} text-center pb-0.5`}>{label}</div>
      <Chip label="Total"   value={split.total}
        active={isSel("total")}
        muted={split.total === 0}
        palette={palette} tier="total"
        onClick={() => onSelect({ lane: laneKey, split: splitKey, tier: "total" })} />
      <Chip label="LOIs"    value={split.lois}
        active={isSel("loi")}
        muted={split.lois === 0}
        palette={palette} tier="lois"
        onClick={() => onSelect({ lane: laneKey, split: splitKey, tier: "loi" })} />
      <Chip label="B&F"     value={split.bestFinal}
        active={isSel("bf")}
        muted={split.bestFinal === 0}
        palette={palette} tier="bf"
        onClick={() => onSelect({ lane: laneKey, split: splitKey, tier: "bf" })} />
    </div>
  );
}

function ByStageList({ stages, byStage, laneKey, onSelect, selection, palette }) {
  const rows = stages.filter(s => (byStage[s] || 0) > 0);
  if (rows.length === 0) return null;
  return (
    <div className={`flex flex-col gap-1 mt-2 pt-2 border-t ${palette.divider}`}>
      {rows.map(s => {
        const isSel = selection?.lane === laneKey && selection?.stage === s;
        return (
          <button
            key={s}
            onClick={() => onSelect({ lane: laneKey, stage: s })}
            className={`flex items-center justify-between gap-2 px-2 py-1 rounded text-[11px] transition-colors ${
              isSel ? palette.stageRowActive : palette.stageRowBase
            }`}
          >
            <span>{s}</span>
            <span className={`tabular-nums font-semibold ${isSel ? "" : palette.valueBold}`}>{byStage[s]}</span>
          </button>
        );
      })}
    </div>
  );
}

function Lane({ laneKey, lane, onSelect, selection, splitStages = [], palette }) {
  const isLaneSel = selection?.lane === laneKey && !selection?.split && !selection?.stage;
  return (
    <div className={`flex-1 min-w-[200px] ${palette.laneCard} ${palette.laneAcc[laneKey]} rounded-xl p-3 flex flex-col`}>
      <button
        onClick={() => onSelect({ lane: laneKey })}
        className={`rounded-lg px-3 py-2 mb-3 flex items-center justify-between font-bold text-sm ${palette.laneHdr[laneKey]} ${
          isLaneSel ? "ring-2 ring-pp-red/60" : ""
        }`}
      >
        <span className="uppercase tracking-wider text-xs">{LANE_NAME[laneKey]}</span>
        <span className="text-lg tabular-nums">{lane.total}</span>
      </button>

      {laneKey !== "tracking" && (
        <div className="grid grid-cols-2 gap-2">
          <LaneSplitCol label="Off-Market" split={lane.offMarket} laneKey={laneKey} splitKey="off" onSelect={onSelect} selection={selection} palette={palette} />
          <LaneSplitCol label="On-Market"  split={lane.onMarket}  laneKey={laneKey} splitKey="on"  onSelect={onSelect} selection={selection} palette={palette} />
        </div>
      )}

      {splitStages.length > 0 && (
        <ByStageList stages={splitStages} byStage={lane.byStage || {}} laneKey={laneKey} onSelect={onSelect} selection={selection} palette={palette} />
      )}
    </div>
  );
}

export default function ReviewPipelineFunnel({ funnel, onSelect, selection, dateLabel, preProxyLabels = [], variant = "dark" }) {
  if (!funnel) return null;
  const palette = PALETTES[variant] || PALETTES.dark;
  const { total, lanes } = funnel;

  return (
    <div className={`${palette.root} rounded-2xl p-4`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
        <div className="flex items-baseline gap-3">
          <div className={`text-xs uppercase tracking-wider ${palette.labelDim} font-bold`}>Total Opportunities</div>
          <div className={`text-4xl font-bold ${palette.total} tabular-nums`}>{total}</div>
          {dateLabel && <span className={`text-xs ${palette.totalNote}`}>· {dateLabel}</span>}
        </div>
        {preProxyLabels.length > 0 && (
          <div className={`text-[11px] ${palette.totalNote}`}>
            Includes pre-Proxy aggregate: {preProxyLabels.join(", ")}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <Lane laneKey="dead"     lane={lanes.dead}     onSelect={onSelect} selection={selection} splitStages={DEAD_STAGES} palette={palette} />
        <Lane laneKey="tracking" lane={lanes.tracking} onSelect={onSelect} selection={selection} palette={palette} />
        <Lane laneKey="active"   lane={lanes.active}   onSelect={onSelect} selection={selection} splitStages={ACTIVE_STAGES_SHOWN} palette={palette} />
      </div>

      {(selection && (selection.lane || selection.stage)) && (
        <div className={`mt-3 flex items-center gap-2 text-xs ${palette.labelDim}`}>
          <span>Filter:</span>
          <span className={`px-2 py-0.5 rounded-full font-semibold ${palette.tagBg}`}>
            {selection.lane && LANE_NAME[selection.lane]}
            {selection.split && ` · ${selection.split === "off" ? "Off-Market" : "On-Market"}`}
            {selection.tier === "loi" && " · LOI"}
            {selection.tier === "bf"  && " · Best & Final"}
            {selection.stage && ` · ${selection.stage}`}
          </span>
          <button onClick={() => onSelect({})} className={`underline ${palette.tagBtn}`}>clear</button>
        </div>
      )}
    </div>
  );
}
